import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CoachButton, ErrorNote, Eyebrow, GoldSeal } from '../components/primitives';
import { apiErrorMessage, fmtPeso, performGatewayHandoff, store } from '../lib/api';
import {
  canPayAgain,
  learningLanding,
  orderDelivered,
  orderKind,
  orderOutcome,
  orderPollMs,
  type OrderKind,
} from '../lib/order-outcome';
import { tierLabel } from '../lib/pro';
import { qk, useEntitlement, useProfile, useTierLabels } from '../lib/queries';

/**
 * Where a payment lands: the gateway returns the member here, and a manual
 * payment starts here.
 *
 * WHAT IT WAITS FOR depends on what was bought (`orderKind`, from the order's
 * `unlocks`). For the coaching plan it is the entitlement, not the order: an
 * order reaching `completed` OPENS the window, but the promise made to the
 * member is "you are on Pro", so `paid` keeps polling until the plan reads back
 * as Pro. A course or subscription is open once the order COMPLETES, and goods
 * are done once paid.
 *
 * A FAILED online payment leaves the order payable, so which screen to show is
 * decided by `orderOutcome` from the gateway attempt as well as the order.
 */

/** How long a member back from the gateway waits before being offered another try. */
const PAY_AGAIN_AFTER_MS = 30_000;

export function OrderResultPage() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const profile = useProfile();
  const entitlement = useEntitlement();
  const labels = useTierLabels();

  const order = useQuery({
    queryKey: qk.order(id),
    queryFn: () => store.order(id),
    enabled: !!id,
    refetchInterval: (q) => orderPollMs(q.state.data),
  });

  // Pays THIS order again: the initiate route takes a payable order back to
  // the gateway, so a retry never leaves a second unpaid order behind.
  const pay = useMutation({
    mutationFn: async () => performGatewayHandoff(await store.initiateGateway(id)),
    // The failure toasts globally; re-read so the screen shows the order as it
    // really is (paid meanwhile, or cancelled by the unpaid sweep).
    onError: () => void qc.invalidateQueries({ queryKey: qk.order(id) }),
  });

  const status = order.data?.status ?? null;
  const attempt = order.data?.gatewayPayment?.status ?? null;
  const isPro = !!entitlement.data && entitlement.data.tier !== 'free';

  // Re-read the plan on every status change: the window opens server-side the
  // moment the order completes, and nothing tells the app but this.
  useEffect(() => {
    if (!status) return;
    void qc.invalidateQueries({ queryKey: qk.entitlement });
    void qc.invalidateQueries({ queryKey: qk.kala });
    // A course or subscription opens on the same transition: Learn must show it.
    void qc.invalidateQueries({ queryKey: qk.myCourses });
    void qc.invalidateQueries({ queryKey: qk.mySubscriptions });
  }, [status, qc]);

  // Nothing moved for a while after coming back: let the member try again.
  const [stalled, setStalled] = useState(false);
  useEffect(() => {
    setStalled(false);
    const timer = window.setTimeout(() => setStalled(true), PAY_AGAIN_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [id, status, attempt]);

  // Proof of an offline payment, uploaded here rather than by sending the
  // member out to the store on another host.
  const [proof, setProof] = useState<File | null>(null);
  // The reference number off the receipt. The admin types the one they read
  // from the picture and the server matches the two; without the member's
  // value the payment cannot be verified, only approved "anyway".
  const [reference, setReference] = useState('');
  const [proofError, setProofError] = useState<unknown>(null);
  const [proofSent, setProofSent] = useState(false);
  const sendProof = useMutation({
    mutationFn: () =>
      store.uploadProof({
        orderId: id!,
        method: order.data!.paymentMethodCode ?? '',
        // The order's own total and currency: the amount owed is not the
        // member's to retype, and a mismatch is what admins reject.
        amount: order.data!.total,
        currency: 'PHP',
        file: proof!,
        reference: reference.trim(),
      }),
    onSuccess: async () => {
      setProofSent(true);
      await qc.invalidateQueries({ queryKey: qk.order(id!) });
    },
    onError: setProofError,
  });

  const onboarded = Boolean(profile.data?.profile.onboardingCompletedAt);
  const kind: OrderKind = order.data ? orderKind(order.data) : 'coaching';
  // Where "not now" leads: back to where this kind of purchase lives.
  const next =
    kind === 'learning'
      ? '/learn'
      : kind === 'credits'
        ? '/credits'
        : kind === 'goods'
          ? '/shop/orders'
          : onboarded
            ? '/home'
            : '/blueprint';
  const words = COPY[kind];

  if (order.isPending) {
    return <Shell eyebrow="Payment">Checking your payment…</Shell>;
  }
  if (order.isError) {
    return (
      <div className="mx-auto w-full max-w-md px-5 pt-16">
        <ErrorNote error={order.error} retry={() => void order.refetch()} />
      </div>
    );
  }

  const total = fmtPeso(order.data.total);
  const returned = Boolean(params.get('pay'));
  const delivered = orderDelivered(order.data, { isPro });
  const outcome = orderOutcome(order.data, { returned, delivered });
  const later = (
    <CoachButton kind="quiet" block onClick={() => navigate(next, { replace: true })}>
      Not now
    </CoachButton>
  );

  if (outcome === 'cancelled') {
    return (
      <Shell eyebrow="Payment" title="This payment did not go through">
        <p>Nothing was charged. You can try again whenever you are ready.</p>
        <div className="mt-8 space-y-3">
          <CoachButton kind="gold" block onClick={() => navigate(words.retry)}>
            Try again
          </CoachButton>
          {later}
        </div>
      </Shell>
    );
  }

  if (outcome === 'success' && kind === 'learning') {
    const unlocks = order.data.unlocks;
    const oneCourse = unlocks?.courseIds.length === 1 ? unlocks.courseIds[0] : null;
    const subscription = (unlocks?.subscriptionCreatorIds.length ?? 0) > 0;
    return (
      <Shell
        eyebrow="Learn"
        title={
          subscription
            ? 'Your subscription is on'
            : oneCourse
              ? 'Your course is ready'
              : 'You are in'
        }
        seal
      >
        <p>
          {subscription
            ? 'Every course it covers is open now. You will find them under Browse, marked Included.'
            : 'It is open now in Learn, and your progress saves as you go.'}
        </p>
        <div className="mt-8">
          <CoachButton
            kind="gold"
            block
            onClick={() => navigate(learningLanding(unlocks), { replace: true })}
          >
            {subscription ? 'Choose a course' : 'Start learning'}
          </CoachButton>
        </div>
      </Shell>
    );
  }

  if (outcome === 'success' && kind === 'goods') {
    return (
      <Shell eyebrow="Shop" title="Thank you, your order is in" seal>
        <p>Your payment is confirmed. You can follow the order under Orders as it moves.</p>
        <div className="mt-8 space-y-3">
          <CoachButton
            kind="gold"
            block
            onClick={() => navigate('/shop/orders', { replace: true })}
          >
            View my orders
          </CoachButton>
          <CoachButton kind="quiet" block onClick={() => navigate('/shop', { replace: true })}>
            Back to the store
          </CoachButton>
        </div>
      </Shell>
    );
  }

  if (outcome === 'success') {
    const planName = tierLabel(
      entitlement.data && entitlement.data.tier !== 'free' ? entitlement.data.tier : 'pro',
      labels,
    );
    const until = entitlement.data?.endsAt
      ? new Date(entitlement.data.endsAt).toLocaleDateString(undefined, { dateStyle: 'long' })
      : null;
    return (
      <Shell eyebrow={planName} title={`You are on ${planName}`} seal>
        <p>
          Everything {planName} adds is open now{until ? `, through ${until}` : ''}. Your coach
          already knows.
        </p>
        <div className="mt-8">
          <CoachButton kind="gold" block onClick={() => navigate(next, { replace: true })}>
            {onboarded ? 'Back to today' : 'Create my blueprint'}
          </CoachButton>
        </div>
      </Shell>
    );
  }

  if (outcome === 'gateway_failed' || outcome === 'gateway_unpaid') {
    const failed = outcome === 'gateway_failed';
    return (
      <Shell
        eyebrow="Payment"
        title={failed ? 'This payment did not go through' : 'Finish your payment'}
      >
        <p>
          {failed
            ? 'Nothing was charged, and your order is still open. You can try again now or whenever you are ready.'
            : `Your order is saved. Pay online and ${words.opens} as soon as the payment is confirmed.`}
        </p>
        <div className="mt-8 space-y-3">
          <CoachButton kind="gold" block disabled={pay.isPending} onClick={() => pay.mutate()}>
            {pay.isPending ? 'Opening…' : failed ? `Try again, ${total}` : `Pay ${total}`}
          </CoachButton>
          {later}
        </div>
      </Shell>
    );
  }

  if (outcome === 'manual_pending') {
    const label = order.data.paymentMethodLabel ?? 'your chosen method';
    return (
      <Shell eyebrow="Payment" title={`Send ${total} through ${label}`}>
        <p>
          {words.manual} Send the amount, then upload your receipt here and we will take it from
          there.
        </p>
        {proofSent ? (
          <p className="mt-8 border border-[rgba(176,141,63,0.4)] px-4 py-3 text-[0.9rem] leading-relaxed text-ink/85">
            Got it. We are checking your payment now, and {words.opens} once it is confirmed.
          </p>
        ) : (
          <div className="mt-8 space-y-3">
            <label className="eyebrow block" htmlFor="proof-reference">
              Reference or receipt number
            </label>
            <input
              id="proof-reference"
              type="text"
              inputMode="text"
              autoComplete="off"
              maxLength={120}
              placeholder="e.g. 1234567890"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              className="block w-full border border-[rgba(16,32,26,0.22)] bg-background px-4 py-3 text-[0.95rem] text-ink placeholder:text-ink/40"
            />
            <p className="text-[0.82rem] leading-relaxed text-ink/70">
              The number on your {label} receipt. We match it to your payment to confirm it.
            </p>
            <label className="eyebrow block" htmlFor="proof-file">
              Screenshot or photo of your receipt
            </label>
            <input
              id="proof-file"
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => setProof(e.target.files?.[0] ?? null)}
              className="block w-full text-[0.9rem] text-ink file:mr-3 file:border file:border-[rgba(16,32,26,0.22)] file:bg-background file:px-4 file:py-2 file:font-mono file:text-[0.68rem] file:uppercase file:tracking-[0.14em] file:text-ink"
            />
            {proofError != null && (
              <p className="border border-[rgba(176,141,63,0.4)] px-4 py-3 text-[0.9rem] text-ink/85">
                {apiErrorMessage(proofError)}
              </p>
            )}
            <CoachButton
              kind="gold"
              block
              disabled={!proof || !reference.trim() || sendProof.isPending}
              onClick={() => {
                setProofError(null);
                sendProof.mutate();
              }}
            >
              {sendProof.isPending ? 'Sending' : 'Upload my proof'}
            </CoachButton>
            <CoachButton kind="quiet" block onClick={() => navigate(next, { replace: true })}>
              I will do this later
            </CoachButton>
          </div>
        )}
      </Shell>
    );
  }

  // Waiting. An over-the-counter payment can take days to arrive; say so
  // rather than promising a moment.
  const overCounter = attempt === 'pending';
  return (
    <Shell
      eyebrow="Payment"
      title={
        overCounter
          ? 'Waiting for your payment'
          : returned
            ? 'Confirming your payment'
            : 'Almost there'
      }
    >
      <p>
        {status === 'paid'
          ? `Payment received. ${capitalize(words.opens)} in a moment.`
          : overCounter
            ? `${capitalize(words.opens)} as soon as the payment reaches us. A payment made over the counter can take a while to arrive, so you can close this page.`
            : 'This page updates by itself as soon as your payment is confirmed.'}
      </p>
      {stalled && canPayAgain(order.data) && (
        <div className="mt-6 space-y-3">
          <p>
            Still waiting? If the payment page closed before you finished, you can open it again.
          </p>
          <CoachButton kind="outline" block disabled={pay.isPending} onClick={() => pay.mutate()}>
            {pay.isPending ? 'Opening…' : 'Pay again'}
          </CoachButton>
        </div>
      )}
      <p className="mt-6">
        <CoachButton kind="quiet" onClick={() => navigate(next, { replace: true })}>
          Continue in the app
        </CoachButton>
      </p>
    </Shell>
  );
}

function Shell({
  eyebrow,
  title,
  seal,
  children,
}: {
  eyebrow: string;
  title?: string;
  seal?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center px-5 py-12">
      {seal && <GoldSeal size={44} />}
      <Eyebrow className={seal ? 'mb-3 mt-6' : 'mb-3'}>{eyebrow}</Eyebrow>
      {title && (
        <h1 className="font-display text-[1.9rem] font-semibold leading-[1.1] text-ink">{title}</h1>
      )}
      <div className="mt-3 text-[0.95rem] leading-relaxed text-ink/70">{children}</div>
    </div>
  );
}

/** The words that change with what was bought. */
const COPY: Record<OrderKind, { opens: string; manual: string; retry: string }> = {
  coaching: {
    opens: 'your plan opens',
    manual: 'This opens as soon as the payment is confirmed.',
    retry: '/upgrade',
  },
  learning: {
    opens: 'your access opens',
    manual: 'Your access opens as soon as the payment is confirmed.',
    retry: '/learn/browse',
  },
  credits: {
    opens: 'your credits are in',
    manual: 'Your credits are added as soon as the payment is confirmed.',
    retry: '/credits',
  },
  goods: {
    opens: 'your order is confirmed',
    manual: 'Your order is confirmed as soon as the payment is.',
    retry: '/shop',
  },
};

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
